import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../context/AppContext';
import { HiXMark, HiCheckCircle, HiLightBulb } from 'react-icons/hi2';
import { wizardsData } from '../../data/wizardsData';

const WizardModal = ({ wizardId, onClose }) => {
  const { t } = useTranslation();
  const { getWizardProgress, updateWizardProgress } = useApp();
  const wizard = wizardsData[wizardId];
  const progress = getWizardProgress(wizardId);
  
  const [currentStep, setCurrentStep] = useState(progress.currentStep || 1);

  useEffect(() => {
    // Update progress when step changes
    updateWizardProgress(wizardId, currentStep, false);
  }, [currentStep, wizardId, updateWizardProgress]);

  const handleNext = () => {
    if (currentStep < wizard.steps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    updateWizardProgress(wizardId, wizard.steps, true);
    onClose();
  };

  const stepKey = `wizards.${wizardId}.step${currentStep}`;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl z-50 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden animate-modal">
        {/* Header */}
        <div className="bg-teal-600 dark:bg-teal-700 text-white p-4 md:p-6 flex-shrink-0 rounded-t-2xl">
          <div className="flex items-start justify-between mb-3 md:mb-4">
            <h2 className="text-xl md:text-2xl font-bold pe-8">
              {t(`wizards.${wizardId}.title`)}
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 flex-shrink-0"
            >
              <HiXMark className="h-6 w-6" />
            </button>
          </div>
          
          {/* Progress */}
          <div>
            <p className="text-teal-100 text-sm mb-2">
              {t('wizards.stepOf', { current: currentStep, total: wizard.steps })}
            </p>
            <div className="w-full bg-teal-700 dark:bg-teal-800 rounded-full h-2 overflow-hidden">
              <div
                className="bg-white h-full rounded-full transition-all duration-300"
                style={{ width: `${(currentStep / wizard.steps) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="p-4 md:p-6 overflow-y-auto flex-1">
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-teal-100 dark:bg-teal-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-teal-700 dark:text-teal-300 font-bold">{currentStep}</span>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t(`${stepKey}.title`)}
              </h3>
            </div>
            
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {t(`${stepKey}.content`)}
            </p>
          </div>

          {/* Documents needed */}
          {t(`${stepKey}.documents`, { returnObjects: true, defaultValue: [] }).length > 0 && (
            <div className="mb-4 p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                {t('wizards.documentsNeeded')}
              </h4>
              <ul className="space-y-1">
                {t(`${stepKey}.documents`, { returnObjects: true }).map((doc, idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    <HiCheckCircle className="h-5 w-5 text-teal-600 dark:text-teal-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 dark:text-gray-300">{doc}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Tip */}
          {t(`${stepKey}.tip`, { defaultValue: '' }) && (
            <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border-s-4 border-orange-400 dark:border-orange-500 rounded-e-lg">
              <div className="flex items-start gap-2">
                <HiLightBulb className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-orange-900 dark:text-orange-300 text-sm mb-1">{t('wizards.tipLabel')}</p>
                  <p className="text-orange-800 dark:text-orange-200 text-sm">{t(`${stepKey}.tip`)}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="border-t border-gray-200 dark:border-gray-700 p-4 md:p-6 flex items-center justify-between bg-white dark:bg-gray-800 flex-shrink-0">
          <button
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="px-3 md:px-4 py-2 text-sm md:text-base text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('wizards.previous')}
          </button>

          <div className="flex items-center gap-1.5 md:gap-2">
            {[...Array(wizard.steps)].map((_, idx) => (
              <div
                key={idx}
                className={`h-2 w-2 rounded-full transition-colors ${
                  idx + 1 === currentStep
                    ? 'bg-teal-600 dark:bg-teal-500 w-3 md:w-4'
                    : idx + 1 < currentStep
                    ? 'bg-teal-400 dark:bg-teal-600'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              />
            ))}
          </div>

          {currentStep < wizard.steps ? (
            <button
              onClick={handleNext}
              className="px-4 md:px-6 py-2 text-sm md:text-base bg-teal-600 dark:bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 dark:hover:bg-teal-700"
            >
              {t('wizards.next')}
            </button>
          ) : (
            <button
              onClick={handleFinish}
              className="px-4 md:px-6 py-2 text-sm md:text-base bg-green-600 dark:bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 dark:hover:bg-green-700"
            >
              {t('wizards.finish')}
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default WizardModal;
